import React, { useEffect, useMemo, useState } from "react"
import { Alert, Button, Input, Select, Space, Tag } from "antd"
import StickyTable from "../table/StickyTable"   // tog'a #4695/#4703: hamma jadvalda sarlavha va «Итого» qotadi
import { CheckOutlined } from "@ant-design/icons"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { request } from "../../utils/config"
import Drawer from "../drawer"
import useQueryParams from "../../hooks/useQueryParams"
import { nonNeg } from "../../utils/nonNeg"
import { useIsMainSystem } from "../../utils/mainSystem"   // tog'a #4104

// tog'a #2420 (2026-09-23): «tovar perevod qilganda perevodni OSTATKA jadvalidan olishi kerak, nega zakupka jadvalidan olyapti».
// Ilgari tanlash oynasi «Закупки» jurnalining o'zi edi (PurchaseSelect): qoldig'i 0 bo'lgan partiyalar ham turardi, son esa
// zakupkadagi miqdor edi. Endi oyna faqat QOLDIG'I BOR partiyalarni ko'rsatadi — qoldiq serverda partiya daftaridan
// (Residues/AvailableBatches → BatchLedger), ya'ni perevod chegarasi va «Остатки» bilan bitta hisob.

export type ResidueBatch = {
  purchaseId: number
  productId: number
  name: string
  article?: string
  barCode?: string
  unitName?: string
  batchNumber?: string
  document?: string
  warehouseId?: number
  warehouseName?: string
  left: number
  priceUSD?: number
  datePurchase?: string
}

export default function ResidueBatchPicker({ onSelectRows }: { onSelectRows: (rows: ResidueBatch[]) => void }) {
  const { t } = useTranslation()
  const query = useQueryParams()
  const bm = useIsMainSystem()   // tog'a #4104: hamkor tizimida partiya ustuni yo'q (qator tanlash o'zgarmaydi)
  const ochiq = query.has("residueBatches")
  const [qidiruv, setQidiruv] = useState("")
  const [izlash, setIzlash] = useState("")
  const [ombor, setOmbor] = useState<number>(0)
  const [tanlangan, setTanlangan] = useState<Record<number, ResidueBatch>>({})

  // yozish tugagach 400 ms kutib so'raladi (har harfga so'rov ketmasin)
  useEffect(() => {
    const id = setTimeout(() => setIzlash(qidiruv.trim()), 400)
    return () => clearTimeout(id)
  }, [qidiruv])

  const { data, isFetching, isError } = useQuery(
    ["RESIDUE_BATCHES", ombor, izlash],
    async () => (await request({ url: "Residues/AvailableBatches", params: { warehouseId: ombor || 0, search: izlash || undefined } })).data,
    { enabled: ochiq, keepPreviousData: true }
  )
  const qatorlar: ResidueBatch[] = data?.items || []

  // sklad filtri variantlari — birinchi (filtrsiz) javobdagi skladlardan; tanlangan sklad ro'yxatdan yo'qolmasin
  const [omborlar, setOmborlar] = useState<{ value: number; label: string }[]>([])
  useEffect(() => {
    if (ombor !== 0 || izlash) return
    const m = new Map<number, string>()
    qatorlar.forEach((r) => r.warehouseId && m.set(r.warehouseId, r.warehouseName || "#" + r.warehouseId))
    setOmborlar(Array.from(m, ([value, label]) => ({ value, label })))
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const tanlanganIds = useMemo(() => Object.keys(tanlangan).map(Number), [tanlangan])

  function tasdiqlash() {
    onSelectRows(Object.values(tanlangan))
    query.remove("residueBatches")
  }

  const columns = [
    { title: t("product"), dataIndex: "name", key: "name", render: (v: string) => <b>{v}</b> },
    { title: t("barCode"), dataIndex: "barCode", key: "barCode", width: 130, render: (v: string) => v || "—" },
    { title: t("article"), dataIndex: "article", key: "article", width: 120, render: (v: string) => v || "—" },
    ...(bm ? [{ title: t("numberParty"), key: "batch", width: 120, render: (_: any, r: ResidueBatch) => r.batchNumber || r.document || "—" }] : []),   // tog'a #4104: faqat glavniy tizimda
    { title: t("warehouse"), dataIndex: "warehouseName", key: "warehouseName", width: 150 },
    {
      title: t("residue"), dataIndex: "left", key: "left", width: 110, align: "right" as const,
      render: (v: number, r: ResidueBatch) => <Tag color="green" style={{ fontWeight: 700, margin: 0 }}>{nonNeg(v)}{r.unitName ? " " + r.unitName : ""}</Tag>,
    },
    { title: t("priceUSD"), dataIndex: "priceUSD", key: "priceUSD", width: 100, align: "right" as const, render: (v: number) => (v ? "$" + v : "—") },
  ]

  return (
    <>
      <Button block size="large" onClick={() => { setTanlangan({}); query.set("residueBatches", "true") }} data-testid="residue-batch-open">
        {t("selectFromResidues", "Выбрать из остатков")}
      </Button>
      <Drawer
        matcher="residueBatches"
        zIndex={1020}
        width="100%"
        title={t("selectFromResidues", "Выбрать из остатков")}
        extra={
          <Button type="primary" icon={<CheckOutlined />} disabled={tanlanganIds.length === 0} onClick={tasdiqlash} data-testid="residue-batch-ok">
            {t("ok")}{tanlanganIds.length ? ` (${tanlanganIds.length})` : ""}
          </Button>
        }
      >
        <Space wrap style={{ marginBottom: 10 }}>
          <Input.Search allowClear placeholder={t("search") + ": " + t("product") + ", " + t("barCode") + ", " + t("article") + (bm ? ", " + t("numberParty") : "")}
            value={qidiruv} onChange={(e) => setQidiruv(e.target.value)} style={{ width: 360, maxWidth: "100%" }} data-testid="residue-batch-search" />
          <Select value={ombor} onChange={setOmbor} style={{ width: 220 }}
            options={[{ value: 0, label: t("allWarehouses", "Все склады") }, ...omborlar]} />
        </Space>
        {isError && <Alert type="error" showIcon style={{ marginBottom: 8 }} message={t("error")} />}
        {data?.truncated && (
          <Alert type="warning" showIcon style={{ marginBottom: 8 }}
            message={t("residueBatchesTruncated", "Показаны не все товары — уточните поиск или выберите склад")} />
        )}
        <StickyTable
          rowKey="purchaseId"
          size="small"
          bordered
          loading={isFetching}
          dataSource={qatorlar}
          columns={columns as any}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          locale={{ emptyText: t("residueBatchesEmpty", "Нет партий с остатком") }}
          rowSelection={{
            type: "checkbox",
            preserveSelectedRowKeys: true,
            selectedRowKeys: tanlanganIds,
            onChange: (_keys, rows) => {
              // sahifa/qidiruv almashsa ham avval tanlanganlar saqlanadi (preserveSelectedRowKeys)
              setTanlangan((prev) => {
                const next: Record<number, ResidueBatch> = {}
                ;(_keys as number[]).forEach((k) => {
                  const r = (rows as ResidueBatch[]).find((x) => x && x.purchaseId === k) || prev[k]
                  if (r) next[k] = r
                })
                return next
              })
            },
          }}
        />
      </Drawer>
    </>
  )
}
